import React, { useCallback, useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { AnimatePresence, motion } from "motion/react";

import { useDragScroll } from "~/hooks/use-drag-scroll";
import { cn } from "~/lib/utils";
import { Button } from "./button";

export default function VrodexPopover({
  label,
  value,
  children,
  className,
  buttonClassName,
  open: controlledOpen = false, // controlled open state from parent
  onOpenChange,
  disabled,
}: {
  label?: string;
  value?: string;
  className?: string;
  buttonClassName?: string;
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  disabled?: boolean;
  children?:
    | React.ReactNode
    | ((setPopoverOpen: (isOpen: boolean) => void) => React.ReactNode);
}) {
  // Local state for when onOpenChange is not provided
  const [isOpen, setIsOpen] = useState(controlledOpen);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isControlled = typeof onOpenChange === "function"; // Check if onOpenChange is provided
  const [ref] = useDragScroll();

  // This determines if the popover should be open (controlled or local state)
  const popoverOpen = isControlled ? controlledOpen : isOpen;

  const handleToggle = () => {
    // Only toggle if not already open
    if (popoverOpen) {
      if (isControlled) {
        onOpenChange(false); // Close if controlled
      } else {
        setIsOpen(false); // Close if uncontrolled
      }
    } else {
      if (isControlled) {
        onOpenChange(true); // Open if controlled
      } else {
        setIsOpen(true); // Open if uncontrolled
      }
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        if (isControlled) {
          onOpenChange(false); // Close if controlled
        } else {
          setIsOpen(false); // Close if uncontrolled
        }
      }
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    if (popoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside, popoverOpen]);

  return (
    <div ref={popoverRef}>
      <Button
        disabled={disabled}
        className={cn(
          "w-full relative p-2 justify-between gap-2",
          buttonClassName,
        )}
        type="button"
        variant="outline"
        onClick={handleToggle}
      >
        {label && <span className="text-xs font-normal">{label}</span>}
        {value && <span className="text-foreground">{value}</span>}
        <ChevronLeftIcon className="-rotate-90" />
      </Button>
      <AnimatePresence>
        {popoverOpen && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(
              "mt-2 z-[999] w-auto shadow border rounded small-scrollbar bg-background absolute p-4 max-h-80 overflow-auto left-0 right-0",
              className,
            )}
          >
            {typeof children === "function"
              ? children(isControlled ? onOpenChange : setIsOpen) // Pass the correct function to children
              : children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
